import Form from "react-bootstrap/Form";

import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { BsTrash } from "react-icons/bs";

import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";

import { Alert, RoleAccessButton } from "@evos/components";
import supportedConfigBuilderKeys from "lib/constants/supportedConfigBuilderKeys.json";

import {
  ConfigBuilderFormArguments,
  zodConfigBuilderFormArguments,
} from "types";
// import type { ApolloError } from "@apollo/client";

type ConfigBuilderProps = {
  userRoles: string[];
  submitSuccess: Array<{ serials: string[]; configItem: string } | null> | null;
  submitErrors: Array<{
    serials: string[];
    configItem: string;
    message?: string;
  } | null> | null;
  submitPending: Array<{ serials: string[]; configItem: string } | null> | null;
  submitLoading: boolean;
  resetSubmitStatuses: () => void;
  onConfigBuilderFormSubmit: (data: ConfigBuilderFormArguments) => void;
};

export default function ConfigBuilder({
  userRoles,
  submitSuccess,
  submitErrors,
  submitPending,
  submitLoading,
  resetSubmitStatuses,
  onConfigBuilderFormSubmit,
}: ConfigBuilderProps) {
  const listOfSerials = Array.from(Array(10000).keys())
    .filter((val) => val !== 0)
    .map((i) => i.toString().padStart(6, "0"));

  const {
    register,
    control,
    getValues,
    handleSubmit,
    reset,
    resetField,
    formState: { isDirty, errors },
  } = useForm<ConfigBuilderFormArguments>({
    defaultValues: {
      configItems: [],
      serialSelectionType: "multiSelect",
      serials: [],
    },
    resolver: zodResolver(zodConfigBuilderFormArguments),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "configItems",
  });

  return (
    <div id="ConfigBuilder">
      <p>Create a list of config items to bulk apply to chargers.</p>
      <Form onSubmit={handleSubmit(onConfigBuilderFormSubmit)}>
        {fields.map((item, index) => {
          const nameErrorMessage =
            errors.configItems?.[index]?.name?.message ?? "";
          const valueErrorMessage =
            errors.configItems?.[index]?.set_value?.message ?? "";

          return (
            <div key={item.id}>
              <div className="d-flex align-items-end gap-4 mb-3">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Config Item Name
                  </Form.Label>
                  <Form.Select
                    {...register(`configItems.${index}.name`)}
                    defaultValue={item.name}
                  >
                    {supportedConfigBuilderKeys.map((name) => (
                      <option
                        disabled={fields
                          .map((field) => field.name)
                          .includes(name)}
                        key={`${item.id}-${name}`}
                        value={name}
                      >
                        {name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label className="fw-semibold">Value</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter value"
                    {...register(`configItems.${index}.set_value`)}
                  />
                </Form.Group>
                <RoleAccessButton
                  variant="outline-danger"
                  userRoles={userRoles}
                  expectedRole="EvosChargerEditor"
                  tooltipId="config-item-remove"
                  onClick={() => remove(index)}
                >
                  <BsTrash />
                </RoleAccessButton>
              </div>
              {nameErrorMessage.length > 0 && (
                <Form.Text className="text-danger">
                  {nameErrorMessage}
                </Form.Text>
              )}
              {valueErrorMessage.length > 0 && (
                <Form.Text className="text-danger">
                  {valueErrorMessage}
                </Form.Text>
              )}
            </div>
          );
        })}
        <RoleAccessButton
          variant="outline-secondary"
          userRoles={userRoles}
          expectedRole="EvosChargerEditor"
          tooltipId="config-item-add"
          onClick={() =>
            append({
              name: supportedConfigBuilderKeys.filter(
                (name) => !fields.map((field) => field.name).includes(name)
              )[0],
              set_value: "",
            })
          }
        >
          + Add Config Item
        </RoleAccessButton>
        <Form.Group className="mt-4">
          <Form.Label className="fw-semibold">Choose Serials</Form.Label>
          <div className="mb-2">
            <Controller
              control={control}
              name="serialSelectionType"
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="d-flex gap-3">
                  <Form.Check
                    inline
                    type="radio"
                    label="Multi-select"
                    value="multiSelect"
                    checked={value === "multiSelect"}
                    onChange={() => {
                      onChange("multiSelect");
                      resetField("serials");
                    }}
                    onBlur={onBlur}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Range"
                    value="range"
                    checked={value === "range"}
                    onChange={() => {
                      onChange("range");
                      resetField("serials");
                    }}
                    onBlur={onBlur}
                  />
                </div>
              )}
            />
          </div>
          <Controller
            control={control}
            name="serials"
            render={({ field: { onChange, onBlur, value } }) =>
              getValues("serialSelectionType") === "multiSelect" ? (
                <Typeahead
                  className="pt-1"
                  id="serials"
                  multiple
                  options={listOfSerials}
                  maxResults={10}
                  allowNew={true}
                  placeholder="Choose serials..."
                  onChange={onChange}
                  onBlur={onBlur}
                  selected={value}
                />
              ) : (
                <div className="d-flex align-items-center gap-3">
                  <Typeahead
                    className="pt-1"
                    id="serials"
                    options={
                      value[1]
                        ? listOfSerials.filter((serial) => serial < value[1])
                        : listOfSerials
                    }
                    maxResults={10}
                    placeholder="Choose serial..."
                    onChange={(selected) => onChange([selected[0], value[1]])}
                    onBlur={onBlur}
                    selected={value[0] ? [value[0]] : []}
                  />
                  <p className="mb-0">to</p>
                  <Typeahead
                    className="pt-1"
                    id="serials"
                    options={
                      value[0]
                        ? listOfSerials.filter((serial) => serial > value[0])
                        : listOfSerials
                    }
                    maxResults={10}
                    placeholder="Choose serial..."
                    onChange={(selected) => onChange([value[0], selected[0]])}
                    onBlur={onBlur}
                    selected={value[1] ? [value[1]] : []}
                  />
                </div>
              )
            }
          />
          {errors.serials != null && (
            <Form.Text className="text-danger">
              {errors.serials?.message}
            </Form.Text>
          )}
        </Form.Group>
        <div className="d-flex align-items-center gap-3 mt-4 mb-4">
          <RoleAccessButton
            variant="outline-secondary"
            userRoles={userRoles}
            expectedRole="EvosChargerEditor"
            tooltipId="config-builder-reset"
            disabled={!isDirty}
            onClick={() => {
              reset();
              resetSubmitStatuses();
            }}
          >
            Reset Form
          </RoleAccessButton>
          <RoleAccessButton
            variant="primary"
            userRoles={userRoles}
            expectedRole="EvosChargerEditor"
            tooltipId="config-builder-submit"
            type="submit"
            disabled={!isDirty || getValues("configItems").length === 0}
            isLoading={submitLoading}
          >
            Submit
          </RoleAccessButton>
        </div>
      </Form>
      {submitSuccess != null && submitSuccess.length > 0 && (
        <Alert
          variant="success"
          className="pb-0"
          message={
            <ul>
              {submitSuccess.map(
                (success) =>
                  success != null && (
                    <li key={success.configItem}>
                      Successfully set <b>{success.configItem}</b> on serial(s):{" "}
                      <b>
                        {success.serials.length === 1
                          ? success.serials[0]
                          : success.serials.join(", ")}
                      </b>
                    </li>
                  )
              )}
            </ul>
          }
        />
      )}
      {submitPending != null && submitPending.length > 0 && (
        <Alert
          variant="info"
          className="pb-0"
          message={
            <ul>
              {submitPending.map(
                (pending) =>
                  pending != null && (
                    <li key={pending.configItem}>
                      Pending status set for <b>{pending.configItem}</b> on
                      serial:{" "}
                      <b>
                        {pending.serials.length === 1
                          ? pending.serials[0]
                          : pending.serials.join(", ")}
                      </b>
                    </li>
                  )
              )}
            </ul>
          }
        />
      )}
      {submitErrors != null && submitErrors.length > 0 && (
        <Alert
          variant="danger"
          className="pb-0"
          message={
            <ul>
              {submitErrors.map(
                (error) =>
                  error != null && (
                    <li key={error.configItem}>
                      Error setting <b>{error.configItem}</b> on serial:{" "}
                      <b>
                        {error.serials.length === 1
                          ? error.serials[0]
                          : error.serials.join(", ")}
                      </b>
                      {error.message != null && error.message.length > 0 && (
                        <> – {error.message}</>
                      )}
                    </li>
                  )
              )}
            </ul>
          }
        />
      )}
    </div>
  );
}
